import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useUpdateMembershipSharePrimaryEmail } from "./hooks/useUpdateMembershipSharePrimaryEmail";
import styles from "./UseDifferentEmail.module.css";
import { useMembershipShareApplication } from "../hooks/useMembershipShareApplication";

interface UseDifferentEmailForm {
  email: string;
}

function UseDifferentEmail() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { application, isLoading, isError } = useMembershipShareApplication();
  const { updateMembershipSharePrimaryEmail, isPending: isUpdatingEmail } =
    useUpdateMembershipSharePrimaryEmail();
  const [conflictError, setConflictError] = useState<string | null>(null);
  const { control, handleSubmit } = useForm<UseDifferentEmailForm>({
    defaultValues: {
      email: application?.primary_applicant?.email ?? "",
    },
  });

  if (isLoading) {
    return (
      <PageLayout isLoading className={styles.pageLayout}>
        {null}
      </PageLayout>
    );
  }

  if (isError || !application) {
    return <ErrorScreen />;
  }

  const onSubmit = async (data: UseDifferentEmailForm) => {
    setConflictError(null);

    const response = await updateMembershipSharePrimaryEmail({ email: data.email });

    if (response.membership_share_email_conflict) {
      setConflictError(
        l10n.getString(
          "membership-share-use-different-email-conflict",
          null,
          "That email is already associated with a different applicant. Please use a different email."
        )
      );
      return;
    }

    if (response.membership_share_verification_required) {
      void navigate("../verify-email");
      return;
    }

    if (response.state === "membership_selection") {
      void navigate("../select");
      return;
    }

    void navigate("../activate");
  };

  return (
    <PageLayout className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="margin--bottom--xs fontFamily--heading">
              {l10n.getString(
                "membership-share-use-different-email-heading",
                null,
                "Use a different email"
              )}
            </h1>
            <div className={styles.subheading}>
              {l10n.getString(
                "membership-share-use-different-email-subheading",
                null,
                "The email on this application is already associated with a different applicant. Enter a different email to continue."
              )}
            </div>
          </div>

          <ContentCard radiusSize="l" kind="bordered" paddingSize="none">
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextInput
                control={control}
                name="email"
                label={l10n.getString("personal-info-email-address-label", null, "Email address")}
                type="email"
              />
              {conflictError ? <div className={styles.error}>{conflictError}</div> : null}
              <Button
                label={l10n.getString("button-label-continue", null, "Continue")}
                type="submit"
                isLoading={isUpdatingEmail}
              />
            </form>
          </ContentCard>
        </div>
      </div>
    </PageLayout>
  );
}

export { UseDifferentEmail };
